import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bell, Mail, MessageSquare, BellRing, Gift, Megaphone, ShoppingBag, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationSettings = ({ userId }) => {
  const [preferences, setPreferences] = useState({
    pushEnabled: false,
    emailEnabled: true,
    smsEnabled: false,
    newOffers: true,
    rewards: true,
    promotions: true,
    orderUpdates: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');

  useEffect(() => {
    checkPushSupport();
    fetchPreferences();
  }, [userId]);

  const checkPushSupport = () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if (supported && 'Notification' in window) {
      setPushPermission(Notification.permission);
    }
  };

  const fetchPreferences = async () => {
    try {
      const response = await axios.get(`${API}/notifications/preferences/${userId}`);
      setPreferences(response.data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const requestPushPermission = async () => {
    if (!pushSupported) {
      toast.error('Las notificaciones push no están soportadas en este navegador');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPushPermission(permission);

      if (permission === 'granted') {
        // Get VAPID public key
        const keyResponse = await axios.get(`${API}/notifications/vapid-key`);
        const publicKey = keyResponse.data.publicKey;

        // Register service worker and subscribe
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey)
        });

        // Send subscription to server
        await axios.post(
          `${API}/notifications/subscribe`,
          { subscription: subscription.toJSON() },
          { headers: { 'X-User-Id': userId } }
        );

        setPreferences({ ...preferences, pushEnabled: true });
        toast.success('¡Notificaciones push activadas!');
      } else {
        toast.error('Permiso de notificaciones denegado');
      }
    } catch (error) {
      console.error('Error enabling push:', error);
      toast.error('Error al activar notificaciones push');
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/notifications/preferences/${userId}`, preferences);
      toast.success('Preferencias guardadas');
    } catch (error) {
      toast.error('Error al guardar preferencias');
    } finally {
      setSaving(false);
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card rounded-2xl p-6 shadow-card border border-border/50"
      data-testid="notification-settings"
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center text-white">
          <Bell className="w-5 h-5" />
        </div>
        <div>
          <h3 className="font-semibold text-lg">Notificaciones</h3>
          <p className="text-sm text-muted-foreground">
            Configura cómo quieres recibir actualizaciones
          </p>
        </div>
      </div>

      {/* Notification Channels */}
      <div className="space-y-4 mb-6">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Canales
        </h4>

        {/* Push Notifications */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
          <div className="flex items-center gap-3">
            <BellRing className="w-5 h-5 text-violet-600" />
            <div>
              <p className="font-medium">Notificaciones Push</p>
              <p className="text-xs text-muted-foreground">
                Recibe alertas instantáneas en tu dispositivo
              </p>
            </div>
          </div>
          {preferences.pushEnabled ? (
            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 rounded-full">
              Activado
            </span>
          ) : pushSupported ? (
            <Button
              size="sm"
              variant="outline"
              onClick={requestPushPermission}
              data-testid="enable-push-btn"
            >
              Activar
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">No soportado</span>
          )}
        </div>

        {/* Email */}
        <ToggleOption
          icon={<Mail className="w-5 h-5 text-blue-600" />}
          title="Email"
          description="Recibe ofertas y actualizaciones por correo"
          enabled={preferences.emailEnabled}
          onChange={(val) => setPreferences({ ...preferences, emailEnabled: val })}
        />

        {/* SMS */}
        <ToggleOption
          icon={<MessageSquare className="w-5 h-5 text-green-600" />}
          title="SMS"
          description="Mensajes de texto para ofertas urgentes"
          enabled={preferences.smsEnabled}
          onChange={(val) => setPreferences({ ...preferences, smsEnabled: val })}
          badge="Premium"
        />
      </div>

      {/* Notification Types */}
      <div className="space-y-4 mb-6">
        <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
          Tipos de Notificaciones
        </h4>

        <ToggleOption
          icon={<Gift className="w-5 h-5 text-pink-600" />}
          title="Nuevas Ofertas"
          description="Cupones y descuentos cerca de ti"
          enabled={preferences.newOffers}
          onChange={(val) => setPreferences({ ...preferences, newOffers: val })}
        />

        <ToggleOption
          icon={<ShoppingBag className="w-5 h-5 text-orange-600" />}
          title="Recompensas"
          description="Puntos ganados y premios disponibles"
          enabled={preferences.rewards}
          onChange={(val) => setPreferences({ ...preferences, rewards: val })}
        />

        <ToggleOption
          icon={<Megaphone className="w-5 h-5 text-purple-600" />}
          title="Promociones"
          description="Ofertas especiales y eventos"
          enabled={preferences.promotions}
          onChange={(val) => setPreferences({ ...preferences, promotions: val })}
        />
      </div>

      {/* Save Button */}
      <Button
        className="w-full"
        onClick={savePreferences}
        disabled={saving}
        data-testid="save-notification-prefs"
      >
        {saving ? (
          <span className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Guardando...
          </span>
        ) : (
          'Guardar Preferencias'
        )}
      </Button>
    </motion.div>
  );
};

const ToggleOption = ({ icon, title, description, enabled, onChange, badge }) => (
  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-xl">
    <div className="flex items-center gap-3">
      {icon}
      <div>
        <div className="flex items-center gap-2">
          <p className="font-medium">{title}</p>
          {badge && (
            <span className="text-[10px] px-1.5 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded-full">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
    </div>
    <button
      onClick={() => onChange(!enabled)}
      className={`relative w-12 h-6 rounded-full transition-colors ${
        enabled ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'
      }`}
      data-testid={`toggle-${title.toLowerCase().replace(/\s/g, '-')}`}
    >
      <span
        className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
          enabled ? 'translate-x-7' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

export default NotificationSettings;
